import { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Loading from "../../../components/Loading";
import Data from '../../../../repository';
import { createServiceSchema } from '../../../../validation/schemas';

import styles from '../../../../styles/Admin.module.scss';

function UpdateServiceForm() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [serverError, setServerError] = useState(null);
	const [image, setImage] = useState('');
	const [uploadingImage, setUploadingImage] = useState(false);
	const [imageError, setImageError] = useState(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm({
		resolver: yupResolver(createServiceSchema),
		defaultValues: {
			name: '',
			description: '',
			duration: 30,
			bufferTime: 0,
			price: 0,
			refundWindow: 0,
			depositAmount: 0,
			category: '',
			isPublished: false
		}
	});

	useEffect(() => {
		fetchService();
	}, [id]);

	const fetchService = async () => {
		try {
			const res = await Data.fetchService(id);
			const service = res.data;
			reset({
				name: service.name || '',
				description: service.description || '',
				duration: service.duration || 30,
				bufferTime: service.bufferTime || 0,
				price: service.price || 0,
				refundWindow: service.refundWindow ?? 0,
				depositAmount: service.depositAmount ?? 0,
				category: service.category || '',
				isPublished: service.isPublished
			});
			setImage(service.image || '');
		} catch (error) {
			setServerError('Error fetching service');
			console.error('Error fetching service:', error);
		}
		setLoading(false);
	}

	const onSubmit = async (data) => {
		setSaving(true);
		setServerError(null);

		try {
			await Data.updateService(id, {
				...data,
				duration: Number(data.duration),
				bufferTime: Number(data.bufferTime) || 0,
				price: Number(data.price),
				refundWindow: Number(data.refundWindow) || 0,
				depositAmount: Number(data.depositAmount) || 0,
				isPublished: data.isPublished === 'true' || data.isPublished === true,
				image: image || ''
			});
			navigate('/admin/services');
		} catch (error) {
			setServerError(error.response?.data?.message || 'Failed to update service. Please try again.');
			setSaving(false);
		}
	}

	const handleImageUpload = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setUploadingImage(true);
		setImageError(null);
		try {
			const formData = new FormData();
			formData.append('image', file);
			const response = await Data.uploadImage(formData);
			const imageUrl = response?.data?.data?.Location;
			if (!imageUrl) throw new Error('Image upload did not return a valid URL.');
			setImage(imageUrl);
		} catch (error) {
			setImageError('Failed to upload image. Please try again.');
		} finally {
			setUploadingImage(false);
			event.target.value = '';
		}
	}

	if (loading) return <Loading button={false} />;

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
			<div>
				<p>Service Name: *</p>
				<input
					type="text"
					{...register('name')}
					placeholder="e.g., General Consultation"
					className={errors.name ? 'input-error' : ''}
				/>
				{errors.name && <span className="form-field-error">{errors.name.message}</span>}
			</div>

			<div>
				<p>Description:</p>
				<textarea
					{...register('description')}
					placeholder="Describe this service..."
					rows={4}
					style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
				/>
			</div>

			<div>
				<p>Duration (minutes): *</p>
				<input
					type="number"
					{...register('duration')}
					min={5}
					className={errors.duration ? 'input-error' : ''}
				/>
				{errors.duration && <span className="form-field-error">{errors.duration.message}</span>}
			</div>

			<div>
				<p>Buffer Time (minutes after service):</p>
				<input
					type="number"
					{...register('bufferTime')}
					min={0}
				/>
			</div>

			<div>
				<p>Price (in pence, e.g., 5000 = £50.00): *</p>
				<input
					type="number"
					{...register('price')}
					min={0}
					className={errors.price ? 'input-error' : ''}
				/>
				{errors.price && <span className="form-field-error">{errors.price.message}</span>}
			</div>

			<div>
				<p>Cancellation Window (hours before booking):</p>
				<input
					type="number"
					{...register('refundWindow')}
					min={0}
				/>
			</div>

			<div>
				<p>Deposit Amount (in pence, 0 = disabled, e.g. 1000 = £10.00):</p>
				<input
					type="number"
					{...register('depositAmount')}
					min={0}
				/>
			</div>

			<div>
				<p>Category:</p>
				<input
					type="text"
					{...register('category')}
					placeholder="e.g., General, Specialist"
				/>
			</div>

			<div>
				<p>Service Image:</p>
				<div style={{ display: 'grid', gap: '10px' }}>
					<input
						type="file"
						accept="image/*"
						onChange={handleImageUpload}
						disabled={uploadingImage}
					/>
					{uploadingImage && <span className="form-field-error">Uploading image...</span>}
					{imageError && <span className="form-field-error">{imageError}</span>}
					{image ? (
						<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
							<img
								src={image}
								alt="Service"
								style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e7e7e7' }}
							/>
							<button
								type="button"
								className="btn"
								onClick={() => setImage('')}
								disabled={uploadingImage}
								style={{ width: 'auto' }}
							>
								Remove Image
							</button>
						</div>
					) : null}
				</div>
			</div>

			<div>
				<p>Status:</p>
				<select {...register('isPublished')}>
					<option value={false}>Draft (not visible to customers)</option>
					<option value={true}>Published</option>
				</select>
			</div>

			{serverError && <p style={{ color: 'red' }}>{serverError}</p>}

			<button type="submit" className="btn btnPrimary" disabled={saving}>
				{saving ?
					<Loading button={true} />
					:
					`Save Changes`
				}
			</button>
		</form>
	)
}

export default UpdateServiceForm;
